import { createContext, PropsWithChildren, useCallback, useEffect, useReducer } from 'react'
import { ContactInfoWrapper } from '../../wrappers'
import { ContactInfoData } from '../../models'

export const contactInfoWrapper = new ContactInfoWrapper().load()

 enum ContactActions {
   SET_FIRSTNAME,
   SET_LASTNAME,
   SET_EMAIL,
   SET_PHONE,
   SET_EMAIL_DISABLED,
 }

type ContactAction =
   | { type: ContactActions.SET_FIRSTNAME, payload: string }
   | { type: ContactActions.SET_LASTNAME, payload: string }
   | { type: ContactActions.SET_EMAIL, payload: string }
   | { type: ContactActions.SET_PHONE, payload: string }
   | { type: ContactActions.SET_EMAIL_DISABLED, payload: string }

const ContactInfoReducer = (state: ContactInfoData, action: ContactAction) => {
  switch(action.type) {
    case ContactActions.SET_FIRSTNAME:
      return { ...state, firstName: action.payload }
    case ContactActions.SET_LASTNAME:
      return { ...state, lastName: action.payload }
    case ContactActions.SET_EMAIL:
      return { ...state, email: action.payload }
    case ContactActions.SET_EMAIL_DISABLED:
      return { ...state, email: action.payload, emailDisabled: true }
    case ContactActions.SET_PHONE:
      return { ...state, phone: action.payload }
    default:
      return state
  }
}

interface Props {
  data: ContactInfoData,
  errors: () => Promise<Error[] | undefined>,
  setFirstName: (value: string) => void
  setLastName: (value: string) => void
  setEmail: (value: string) => void
  setPhone: (value: string) => void
  validateFirstName: () => Error | undefined
  validateLastName: () => Error | undefined
  validateEmail: () => Promise<Error | undefined>
  validatePhone: () => Error | undefined
}

export const ContactContext = createContext<Props>({
  errors: () => Promise.resolve(undefined),
  setFirstName: (value: string) => {},
  setLastName: (value: string) => {},
  setEmail: (value: string) => {},
  setPhone: (value: string) => {},
  validateFirstName: () => undefined,
  validateLastName: () => undefined,
  validatePhone: () => undefined,
  validateEmail: () => Promise.resolve(undefined),
  data: {}
})
export const ContactProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const contactInfo = contactInfoWrapper.data
  const [state, dispatch] = useReducer(ContactInfoReducer, contactInfo)

  const setFirstName = useCallback((value: string) => {
    contactInfoWrapper.setFirstName(value)
    dispatch({ type: ContactActions.SET_FIRSTNAME, payload: value })
  }, [])

  const setLastName = useCallback((value: string) => {
    contactInfoWrapper.setLastName(value)
    dispatch({ type: ContactActions.SET_LASTNAME, payload: value })
  }, [])

  const setEmail = useCallback((value: string) => {
    contactInfoWrapper.setEmail(value)
    dispatch({ type: ContactActions.SET_EMAIL, payload: value })
  }, [dispatch])
  
  const setEmailDisabled = useCallback((value: string) => {
    contactInfoWrapper.setEmail(value)
    dispatch({ type: ContactActions.SET_EMAIL_DISABLED, payload: value })
  }, [dispatch])

  const setPhone = useCallback((value: string) => {
    contactInfoWrapper.setPhone(value)
    dispatch({ type: ContactActions.SET_PHONE, payload: value })
  }, [dispatch])

  const validateFirstName = useCallback(() => {
    return contactInfoWrapper.validateFirstName()
  }, [])

  const validateLastName = useCallback(() => {
    return contactInfoWrapper.validateLastName()
  }, [])

  const validateEmail = useCallback(async () => {
    return await contactInfoWrapper.validateEmail()
  }, [])

  const validatePhone = useCallback(() => {
    return contactInfoWrapper.validatePhone()
  }, [])

  const errors = useCallback(() => {
    return contactInfoWrapper.errors()
  }, [])

  useEffect(() => {
    const url = new URL(window.location.href)
    const email = url.searchParams.get('email')
    if (email) {
      setEmailDisabled(email)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const data = state
  return (
    <ContactContext.Provider value={{
      setFirstName,
      setLastName,
      setEmail,
      setPhone,
      validateFirstName,
      validateLastName,
      validateEmail,
      validatePhone,
      errors,
      data
    }}>
      {children}
    </ContactContext.Provider>
  )
}